import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import React, { FC, ReactElement } from 'react';
import styled from 'styled-components';
import { Constants } from '../../../common/constants';
import { HormonePhaseViewModel } from '../../../common/model/type/hormone-summary-status';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvHormonePatternDisclosure from '../molecules/OvHormonePatternDisclosure';
import OvHormonePatternMetric from '../molecules/OvHormonePatternMetric';

interface OvHormonePatternSummaryProps {
  follicularPhase: HormonePhaseViewModel | null;
  lutealPhase: HormonePhaseViewModel | null;
}

interface HormoneDefinition {
  key: 'pg' | 'lrE3g';
  name: string;
  fullName: string;
  unit: string;
  accent: string;
  tint: string;
  icon: ReactElement;
}

const HORMONES: HormoneDefinition[] = [
  {
    key: 'pg',
    name: 'PdG',
    fullName: 'Pregnanediol glucuronide',
    unit: Constants.PG_UNIT_OF_MEASURE,
    accent: Colours.OV_ACCENT_INDIGO,
    tint: Colours.OV_PEAK_OVULATION,
    icon: <WavesOutlinedIcon />,
  },
  {
    key: 'lrE3g',
    name: 'E3G',
    fullName: 'Estrone glucuronide',
    unit: Constants.E3G_UNIT_OF_MEASURE,
    accent: Colours.OV_ACCENT_AMBER,
    tint: Colours.OV_HIGH_OVULATION,
    icon: <WaterDropOutlinedIcon />,
  },
];

const OvHormonePatternSummary: FC<OvHormonePatternSummaryProps> = ({
  follicularPhase,
  lutealPhase,
}) => (
  <Section aria-labelledby="hormone-patterns-heading">
    <SectionHeading id="hormone-patterns-heading">
      Hormone patterns
    </SectionHeading>
    <HormoneGrid>
      {HORMONES.map((hormone) => (
        <HormoneCard key={hormone.key}>
          <HormoneHeader>
            <HormoneMark
              $accent={hormone.accent}
              $tint={hormone.tint}
              aria-hidden="true"
            >
              {hormone.icon}
            </HormoneMark>
            <div>
              <HormoneName>{hormone.name}</HormoneName>
              <HormoneFullName>{hormone.fullName}</HormoneFullName>
            </div>
          </HormoneHeader>
          <PhaseComparison>
            <PhaseHeading $gridArea="follicular-heading">
              Follicular phase (pre-ovulation)
            </PhaseHeading>
            <PhaseHeading $gridArea="luteal-heading">
              Luteal phase (post-ovulation)
            </PhaseHeading>
            <OvHormonePatternMetric
              name="Level"
              hormone={hormone.key}
              phase="follicular"
              metric={follicularPhase?.[hormone.key] ?? null}
              unit={hormone.unit}
              gridArea="follicular-level"
              position="level"
            />
            <OvHormonePatternMetric
              name="Level"
              hormone={hormone.key}
              phase="luteal"
              metric={lutealPhase?.[hormone.key] ?? null}
              unit={hormone.unit}
              gridArea="luteal-level"
              position="level"
            />
            <OvHormonePatternMetric
              name="Stability"
              hormone={hormone.key}
              phase="follicular"
              metric={follicularPhase?.[hormone.key] ?? null}
              unit={hormone.unit}
              gridArea="follicular-stability"
              position="stability"
            />
            <OvHormonePatternMetric
              name="Stability"
              hormone={hormone.key}
              phase="luteal"
              metric={lutealPhase?.[hormone.key] ?? null}
              unit={hormone.unit}
              gridArea="luteal-stability"
              position="stability"
            />
          </PhaseComparison>
        </HormoneCard>
      ))}
    </HormoneGrid>
    <OvHormonePatternDisclosure />
  </Section>
);

export default OvHormonePatternSummary;

const Section = styled.section`
  min-width: 0;
`;

const SectionHeading = styled.h3`
  margin: 0 0 0.75rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HormoneGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
`;

const HormoneCard = styled.article`
  min-width: 0;
  padding: 1rem;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  background: ${Colours.WHITE};
`;

const HormoneHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.875rem;
`;

const HormoneMark = styled.span<{ $accent: string; $tint: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: ${Variables.borderRadius.CIRCLE};
  color: ${({ $accent }) => $accent};
  background: ${({ $tint }) => $tint};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const HormoneName = styled.h4`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.LARGE};
`;

const HormoneFullName = styled.span`
  display: block;
  margin-top: 0.125rem;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
`;

const PhaseComparison = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'follicular-heading'
    'follicular-level'
    'follicular-stability'
    'luteal-heading'
    'luteal-level'
    'luteal-stability';
  column-gap: 0.75rem;

  @container hormone-summary (min-width: 460px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'follicular-heading follicular-heading'
      'follicular-level follicular-stability'
      'luteal-heading luteal-heading'
      'luteal-level luteal-stability';
  }

  @container hormone-summary (min-width: 760px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-areas:
      'follicular-heading follicular-heading luteal-heading luteal-heading'
      'follicular-level follicular-stability luteal-level luteal-stability';
  }
`;

const PhaseHeading = styled.h5<{ $gridArea: string }>`
  grid-area: ${({ $gridArea }) => $gridArea};
  min-width: 0;
  margin: ${({ $gridArea }) =>
    $gridArea === 'luteal-heading' ? '0.75rem 0 0' : '0'};
  padding: 0.75rem 0.75rem 0;
  border-radius: ${Variables.borderRadius.MEDIUM}
    ${Variables.borderRadius.MEDIUM} 0 0;
  background: ${Colours.OV_WHITE};
  color: ${Colours.OV_ACCENT_INDIGO};
  font-size: ${Variables.fontSizes.SMALL};
  text-transform: uppercase;
  line-height: ${Variables.lineHeights.LARGE};

  @container hormone-summary (min-width: 760px) {
    margin-top: 0;
  }
`;
